const CLIENT_NAME_MIN_CHARS = 3

export const valueConstants = {
    SUCCESS_COLOR: '#10b981',
    WARNING_COLOR: '#f59e0b',
    ATTENTION_COLOR: '#ff4081;',
    NEUTRAL_COLOR: '#9ca3af',
    WHITE_COLOR: '#ffffff',
    PRIMARY_TEXT_COLOR: '#930a1c',
    HIGH_PTIORITY_ICON: 'priority_high',
    WARNING_ICON: 'warning',
    VERIFIED_ICON: 'verified',
    CHECK_CIRCLE_ICON: 'check_circle',
    CANCEL_ICON: 'cancel',
    TEAM: 'Team',
    UNASSIGNED_CUSTOMERS: 'Unassigned Customers',
    CLIENT_SCORE_HIGH_VALUE: 80,
    CLIENT_SCORE_LOW_VALUE: 50,
    HIGHLIGHT_COLOR: 'yellow',
    HIGHLIGHT_COLOR_RGB: 'rgb(255, 255, 0)',
    ENTER_KEY: 'Enter',
    TAB_KEY: 'Tab',
    NA: '-',
    DEALS_PIPELINE_TITLE: 'My Deals & Pipeline',
    DEALS_LABEL: 'deals',
    TOTAL_PIPELINE_VALUE_LABEL: 'Total Pipeline Value',
    FILTERS_LABEL: 'Filters:',
    ALL_STAGES: 'All Stages',
    ALL_PRODUCTS: 'All Products',
    ALL_CITIES: 'All Cities',
    PAGE_SIZE_OPTIONS: [5, 10, 25, 50],
    DEFAULT_PAGE_SIZE: 10,
    DEFAULT_PAGE: 1,
    EMPTY_TABLE_TOTAL: 0,
    DROPDOWN_WIDTH: '80px',
    DROPDOWN_HEIGHT: '45px',
    DROPDOWN_APPEARANCE: 'outline' as const,
    TEXT_COLOR_DARK: '#1a1a1a',
    CLIENT_NAME_MIN_LENGTH: CLIENT_NAME_MIN_CHARS,
    CLIENT_NAME_MIN_SEARCH_LENGTH: CLIENT_NAME_MIN_CHARS,
    CLIENT_DESCRIPTION_MAX_LENGTH: 500,
    CLIENT_ADDITIONAL_NOTES_MAX_LENGTH: 2500,
    CLIENT_WEBSITE_MAX_LENGTH: 250,
    CONTACT_NAME_MAX_LENGTH: 255,
    CONTACT_EMAIL_MAX_LENGTH: 255,
    MAT_TOOLTIP_DELAY: 500,
    SALES_FUNNEL_DEFAULT_TITLE: 'My Sales Funnel Overview',
    SALES_FUNNEL_DEFAULT_DESCRIPTION:
        'This view represents the number of leads that successfully progressed to the next stage',
    STAGE_CODE_WON: 'SW',
    STAGE_CODE_LOST: 'SL',
    STAGE_CODE_HOLD: 'SH',
    CRITICAL_APPROVALS: 'Critical Approvals',
    ZONE_METRICS_TITLE: 'Zone Metrics',
    ZONE_DEALS_PIPELINE_TITLE: 'Zone Deals & Pipeline',
    CORE_METRICS_TITLE: 'My Core Metrics',
    TEAM_VIEW_QUERY_PARAM: 'team',
    USER_VIEW_QUERY_PARAM: 'user',
    SEARCH_SCHOOL_NAME_PLACEHOLDER: 'Search by school name',
    ACADEMIC_YEAR_QUERY_PARAM: 'academic_year',
    NO_RECORDS_FOUND: 'No Records Found',
    NO_MATCHES_FOUND: 'No Matches Found',
    NO_ORGANIZATIONS_FOUND: 'No customers found',
    CLEAR_SEARCH: 'Clear search',
    CLEAR_FILTERS_LABEL: 'Clear Filters',
    UNASSIGNED_SEARCH_PLACEHOLDER: 'Search by customer, city & state',
}

export const authEndpoints = {
    USER_INFO_PATH: '/orchestrate/v1/user-info',
    LOGOUT_PATH: '/api/authX/logout',
    SESSION_ACTIVITY_PATH: '/api/authX/v1/session-activity',
    SESSION_HEARTBEAT_PATH: '/api/authX/v1/sessions/heartbeat',
    SIGNIN_PATH: '/signin',
}

export const salesFunnelEndpoints = {
    SALES_FUNNEL: '/api/aggregated/v1/users',
}

export const permissionEndpoints = {
    GET_PERMISSIONS: '/api/authX/v1/permissions/app',
}

export const dashboardEndpoints = {
    MONTHLY_LEADERBOARD_PATH: '/api/aggregated/v1/users/{user_id}/monthly-leaderboard',
    LEADS_CREATED_PATH: '/api/aggregated/v1/leads/created',
    USER_APPROVALS_PATH: '/api/aggregated/v1/users/{userId}/approvals',
    REPORTING_HIERARCHY_PATH: '/api/aggregated/v1/users/reporting_hierarchy',
} as const

export const criticalApprovalsConstants = {
    PAGE_LIMIT: 25,
    STATUS_PENDING: 'pending',
} as const

export const userHierarchyConstants = {
    PAGE_LIMIT: 25,
} as const

export const IndexDBConstants = {
    DB_VERSION: 10,
    PERMISSIONS_STORE_DB: 'permissions_store1',
    PERMISSIONS_STORE_KEY: 'permissions',
}

export const IndexDBKeys = {
    USER_PREFERENCES: 3,
}

export const BITWISE_PARSER_VERSION = 'v1'

export const MAX_INT_32 = 2147483647

export const MAX_STUDENT_COUNT = 500_000
export const TOTAL_STUDENTS_MAX_ERROR_MESSAGE = 'Total number of students cannot exceed 5,00,000'

export const MAX_ANNUAL_FEE = 1_300_000
export const MAX_ANNUAL_FEE_ERROR_MESSAGE = 'Annual Fee cannot exceed ₹13,00,000'

export const MAX_LEAD_PROJECTION = 1_000_000_000 // 100 Crore
export const MAX_LEAD_PROJECTION_ERROR_MESSAGE =
    'Lead Projection cannot exceed ₹1,00,00,00,000 (100 Cr)'
export const OTHER_FEE_MAX_PERCENT_ERROR_MESSAGE = 'Cannot exceed 50% of Annual Fee'
/**
 * Indian academic / financial year window used across lead and revenue APIs.
 *
 * - **Starts:** 1 **July** of the academic year label (e.g. AY 2025 → 1 Jul 2025 00:00 local).
 * - **Ends:** 30 **June** of the following calendar year (e.g. AY 2025 → 30 Jun 2026 end-of-day).
 *
 * `monthIndex` is JavaScript `Date` month (0 = January … 6 = July … 5 = June).
 */
export const ACADEMIC_YEAR_BOUNDARY = {
    START: {
        monthIndex: 6, // July
        day: 1,
    },
    END: {
        monthIndex: 5, // June (closing month of the same FY; paired with calendar year = label + 1)
        day: 30,
    },
} as const

/**
 * Current academic year **label** (start calendar year): on or after 1 July → this calendar year; otherwise previous year.
 * @param now - Optional date (for tests)
 * @returns e.g. `2026` when `now` is April 2027 (inside AY 2026–27)
 */
export const getCurrentAcademicYear = (now: Date = new Date()): number => {
    const year = now.getFullYear()

    const startOfAcademicYearForCalendarYear = new Date(
        year,
        ACADEMIC_YEAR_BOUNDARY.START.monthIndex,
        ACADEMIC_YEAR_BOUNDARY.START.day,
        0,
        0,
        0,
        0,
    )
    return now >= startOfAcademicYearForCalendarYear ? year : year - 1
}
